import { create } from 'zustand';
import { useKeycloak } from '@react-keycloak/web';
import { 
  MaterialTrackerItem, 
  MaterialTrackerResponse,
  getProjectMaterials,
  createProjectMaterial,
  updateProjectMaterial,
  deleteProjectMaterial,
  getProjectMaterial
} from '../api/materialTracker';

interface MaterialTrackerStore {
  materials: { [projectId: string]: MaterialTrackerItem[] };
  selectedMaterial: MaterialTrackerItem | null;
  pagination: {
    page: number;
    size: number;
    total: number;
  };
  loading: boolean;
  error: string | null;
  setMaterials: (projectId: string, data: MaterialTrackerResponse) => void;
  setSelectedMaterial: (material: MaterialTrackerItem | null) => void;
  addMaterial: (projectId: string, material: MaterialTrackerItem) => void;
  updateMaterialInStore: (projectId: string, materialId: string, updatedMaterial: MaterialTrackerItem) => void;
  removeMaterial: (projectId: string, materialId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

export const useMaterialTrackerStore = create<MaterialTrackerStore>((set) => ({
  materials: {},
  selectedMaterial: null,
  pagination: {
    page: 1,
    size: 100,
    total: 0
  },
  loading: false,
  error: null,
  setMaterials: (projectId: string, data: MaterialTrackerResponse) => {
    set((state) => ({
      materials: {
        ...state.materials,
        [projectId]: data.results
      },
      pagination: {
        page: data.page,
        size: data.size,
        total: data.total
      }
    }));
  },
  setSelectedMaterial: (material: MaterialTrackerItem | null) => {
    set({ selectedMaterial: material });
  },
  addMaterial: (projectId: string, material: MaterialTrackerItem) => {
    set((state) => ({
      materials: {
        ...state.materials,
        [projectId]: [...(state.materials[projectId] || []), material]
      },
      pagination: {
        ...state.pagination,
        total: state.pagination.total + 1
      }
    }));
  },
  updateMaterialInStore: (projectId: string, materialId: string, updatedMaterial: MaterialTrackerItem) => {
    set((state) => ({
      materials: {
        ...state.materials,
        [projectId]: (state.materials[projectId] || []).map(material =>
          material.id === materialId ? updatedMaterial : material
        )
      }
    }));
  },
  removeMaterial: (projectId: string, materialId: string) => {
    set((state) => ({
      materials: {
        ...state.materials,
        [projectId]: (state.materials[projectId] || []).filter(material => material.id !== materialId)
      },
      pagination: {
        ...state.pagination,
        total: Math.max(0, state.pagination.total - 1)
      }
    }));
  },
  setLoading: (loading: boolean) => {
    set({ loading });
  },
  setError: (error: string | null) => {
    set({ error });
  }
}));

export const useMaterialTrackerPage = (projectId: string) => {
  const { keycloak } = useKeycloak();
  const store = useMaterialTrackerStore();

  const loadMaterialTracker = async (page: number = 1, size: number = 100) => {
    if (!keycloak.token || !projectId) return;

    store.setLoading(true);
    store.setError(null);

    try {
      console.log(`Loading materials for project ${projectId}, page ${page}, size ${size}`);
      const data = await getProjectMaterials(keycloak.token, projectId, page, size);
      console.log('Materials loaded successfully:', data);
      store.setMaterials(projectId, data);
    } catch (error: any) {
      console.error('Error loading materials:', error);
      
      if (error.message?.includes('404')) {
        // Backend endpoint not implemented yet
        store.setError('Material tracking is not yet available for this project. The feature is being implemented.');
        console.warn('Materials endpoint not found - feature may not be implemented on backend yet');
      } else {
        store.setError(error.message || 'Failed to load material tracker');
      }
    } finally {
      store.setLoading(false);
    }
  };

  const createMaterial = async (material: Omit<MaterialTrackerItem, 'id' | 'project_id'>) => {
    if (!keycloak.token || !projectId) return;

    store.setLoading(true);
    store.setError(null);

    try {
      console.log('Creating material for project:', projectId, material);
      const newMaterial = await createProjectMaterial(keycloak.token, projectId, material);
      store.addMaterial(projectId, newMaterial);
      return newMaterial;
    } catch (error: any) {
      console.error('Error creating material:', error);
      
      if (error.message?.includes('404')) {
        store.setError('Material creation is not yet available. The feature is being implemented.');
      } else {
        store.setError(error.message || 'Failed to create material');
      }
      throw error;
    } finally {
      store.setLoading(false);
    }
  };

  const updateMaterial = async (materialId: string, material: Partial<Omit<MaterialTrackerItem, 'id' | 'project_id'>>) => {
    if (!keycloak.token || !projectId) return;

    store.setLoading(true);
    store.setError(null);

    try {
      console.log('Updating material:', materialId, material);
      const updatedMaterial = await updateProjectMaterial(keycloak.token, projectId, materialId, material);
      store.updateMaterialInStore(projectId, materialId, updatedMaterial);
      return updatedMaterial;
    } catch (error: any) {
      console.error('Error updating material:', error);
      
      if (error.message?.includes('404')) {
        store.setError('Material update is not yet available. The feature is being implemented.');
      } else {
        store.setError(error.message || 'Failed to update material');
      }
      throw error;
    } finally {
      store.setLoading(false);
    }
  };

  const deleteMaterial = async (materialId: string) => {
    if (!keycloak.token || !projectId) return;

    store.setLoading(true);
    store.setError(null);

    try {
      console.log('Deleting material:', materialId);
      await deleteProjectMaterial(keycloak.token, projectId, materialId);
      store.removeMaterial(projectId, materialId);
    } catch (error: any) {
      console.error('Error deleting material:', error);
      
      if (error.message?.includes('404')) {
        store.setError('Material deletion is not yet available. The feature is being implemented.');
      } else {
        store.setError(error.message || 'Failed to delete material');
      }
      throw error;
    } finally {
      store.setLoading(false);
    }
  };

  const loadMaterial = async (materialId: string) => {
    if (!keycloak.token || !projectId) return;

    store.setLoading(true);
    store.setError(null);

    try {
      console.log('Loading material:', materialId);
      const material = await getProjectMaterial(keycloak.token, projectId, materialId);
      store.setSelectedMaterial(material);
      return material;
    } catch (error: any) {
      console.error('Error loading material:', error);
      
      if (error.message?.includes('404')) {
        store.setError('Material details are not yet available. The feature is being implemented.');
      } else {
        store.setError(error.message || 'Failed to load material');
      }
      throw error;
    } finally {
      store.setLoading(false);
    }
  };

  return {
    loadMaterialTracker,
    createMaterial,
    updateMaterial,
    deleteMaterial,
    loadMaterial
  };
}; 